/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Теги</h2>
    <p className={styles.description}>
      Основой HTML являются теги, которые создают различные элементы, задающие содержание и структуру итогового
      документа.
    </p>
    <p className={styles.description}>
      <b>Тег (tag, именованная метка, дескриптор)</b> — элемент языка разметки гипертекста. Контент, содержащийся между
      начальным и конечным тегом, отображается и размещается в соответствии со свойствами, указанными в начальном теге.
      Для выделения тегов среди остального текста документа, используются специальные символы - угловые скобки (angle
      brackets).
    </p>
    <p className={styles.description}>
      HTML-документ состоит из дерева HTML-элементов и текста. Каждый элемент обозначается в исходном документе
      открывающим и закрывающим тегом (за редким исключением).
    </p>
    <CopyBlock text="<имя-тега>…</имя-тега>" language="html" showLineNumbers theme={shadesOfPurple} />
    <p className={styles.description}>
      Открывающий тег показывает, где начинается элемент, закрывающий — где заканчивается. Закрывающий тег образуется
      путем добавления слэша <code className={styles.code}>/</code> перед именем тега. Между открывающим и закрывающим
      тегами находится содержимое тега — контент.
    </p>
    <p className={styles.description}>
      Теги могут вкладываться друг в друга, например. При вложении следует соблюдать порядок их закрытия (принцип
      «матрёшки»).
    </p>
    <CopyBlock
      text={`<тег-1>
      <тег-2>
            <тег-3>…</тег-3>
            <тег-4>…</тег-4>
      </тег-2>
</тег-1>
`}
      language="html"
      showLineNumbers
      theme={shadesOfPurple}
    />
    <p className={styles.description}>Теги можно разделить на две категории:</p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <b>Парные теги</b> - теги, состоящие из открывающего тега (например <code className={styles.code}>{`<p>`}</code>
        ) и закрывающего тега ( <code className={styles.code}>{`</p>`}</code>
        ). Парные теги оборачивают контент, позволяя изменять его свойства или вид отображения. Внутри парных тегов
        могут присутствовать другие теги.
      </li>
      <li className={styles.list__item}>
        <b>Одиночные теги</b> - теги состоящие только из открывающего тега (например{' '}
        <code className={styles.code}>{`<img>`}</code>
        ). Одиночные теги создают один элемент на странице, либо служат для изменения свойств документа, подключения
        файлов и т.д. Они получают свой контент или поведение из атрибутов.
      </li>
    </ul>
    <iframe
      height="300"
      scrolling="no"
      title="rotman_html_3_1"
      src="https://codepen.io/rotmanyan/embed/ExwYdEx?default-tab=html%2Cresult&editable=true&theme-id=dark"
      frameBorder="no"
      loading="lazy"
      allowTransparency="true"
      allowFullScreen="true"
    />
    <p className={styles.description}>
      В результате того, что что текст был заключен между различными открывающими и закрывающими тегами, он изменил своё
      начертание (стал полужирным, подчеркнутым или курсивным).
    </p>
    <p className={styles.description}>
      Существует множество тегов которые служат для описания основных элементов веб страницы и их визуального
      оформления, условно теги делятся на следующие типы:
    </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>Теги верхнего уровня</li>
      <li className={styles.list__item}>Теги заголовка документа</li>
      <li className={styles.list__item}>Теги общего назначения</li>
    </ul>
    <p className={styles.description}>Не путайте понятия тег и элемент:</p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <b>Теги</b> — это структура языка HTML, они указывают браузеру какой мы хотим создать элемент.
      </li>
      <li className={styles.list__item}>
        <b>Элементы</b> — это то, из чего уже состоит веб-страница после открытия HTML-документа в браузере.
      </li>
    </ul>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Теги верхнего уровня</h3>
      <p className={styles.description}>
        Эти теги предназначены для формирования структуры веб-страницы и определяют раздел заголовка и тела документа.
      </p>
      <p className={styles.description}>
        <code className={styles.code}>html</code> — корневой элемент документа, как контейнер который заключает в себе
        всё содержимое веб-страницы, включая теги <code className={styles.code}>head</code> и{' '}
        <code className={styles.code}>body</code>.
      </p>
      <p className={styles.description}>
        <code className={styles.code}>head</code> — предназначен для хранения технической информации о странице:
        заголовок, описание, ключевые слова для поисковых машин, кодировку и т.д. Вся эта информация не отображается в
        окне браузера, однако содержит данные, которые указывают браузеру, как следует обрабатывать страницу.
      </p>
      <p className={styles.description}>
        <code className={styles.code}>body</code> — предназначен для хранения содержания веб-страницы, отображаемого в
        окне браузера. Контент, который должен отображаться на странице, следует располагать именно внутри контейнера
        body. К такому контенту относятся текст, изображения, таблицы, списки и др.
      </p>
      <p className={styles.description}>
        В примере приведен набор тегов верхнего уровня и порядок их вложености. Это основа любого HTML-документа, как
        фундамент для дома.
      </p>
      <CopyBlock
        text={`<!DOCTYPE html>
<html>
    <head>
    ...
    </head>
    <body>
    ...
    </body>
</html>`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <p className={styles.description}>
        Обратите внимание на <code className={styles.code}>DOCTYPE</code> перед тегом{' '}
        <code className={styles.code}>html</code>. Этот обязательный элемент кода веб-страницы не является тегом, это
        объявление типа документа. Он предназначен для того, чтобы сообщить браузеру, в какой версии HTML написан
        документ и как его интерпретировать (в данном случае это HTML5). Это гарантирует, что страница будет выглядеть
        единообразно во всех современных браузерах.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Теги заголовка документа</h3>
      <p className={styles.description}>
        К этим тегам относятся элементы, которые располагаются в <code className={styles.code}>head</code>. Они напрямую
        не отображаются в окне браузера, за исключением тега <code className={styles.code}>title</code>, который
        определяет название веб-страницы.
      </p>
      <section className={styles.subsection}>
        <h3 className={styles.h4}>3.2.1 - title</h3>
        <p className={styles.description}>
          Обязательный тег. Текст, размещенный внутри него, отображается в строке заголовка веб-браузера. Длина
          заголовка должна быть не более 60 символов, чтобы полностью поместиться в заголовке. Текст заголовка должен
          содержать максимально полное описание содержимого веб-страницы.
        </p>
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h4}>3.2.2 - meta</h3>
        <p className={styles.description}>
          Используются для хранения информации, предназначенной для браузеров и поисковых систем - установки кодировки
          документа, для передачи информации поисковым системам, для установки ширины и начального масштаба для окна
          просмотра на мобильных устройствах и многое другое. Например, механизмы поисковых систем обращаются к
          метатегам для получения описания сайта, ключевых слов и других данных.
        </p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Метатег</th>
              <th>Описание</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>&lt;meta charset="UTF-8"&gt;</td>
              <td>Документу задается кодировка UTF-8</td>
            </tr>
            <tr>
              <td>&lt;meta name="author" content="Rotman Yan"&gt;</td>
              <td>Метатег, который определяет автора документа.</td>
            </tr>
            <tr>
              <td>&lt;meta name="keywords" content="HTML, тег, метатег"&gt;</td>
              <td>
                Метатег, который определяет ключевые слова документа (незначительно влияет на ранжирование в поисковой
                выдаче)
              </td>
            </tr>
            <tr>
              <td>&lt;meta name="description" content="Страница о тегах"&gt;</td>
              <td>
                Метатег, который определяет описание документа для поисковых систем (незначительно влияет на
                ранжирование в поисковой выдаче)
              </td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h4}>3.2.3 - пример</h3>
        <p className={styles.description}>
          Используем тег <code className={styles.code}>title</code> для задания заголовка и{' '}
          <code className={styles.code}>meta</code> (с атрибутом charset, о атрибутах мы поговорим позже) для указания
          кодировки страницы.
        </p>
        <CopyBlock
          text={`<!DOCTYPE html>
<html>
      <head>
           <meta charset="UTF-8">
           <title>HTML is fun!</title>
      </head>
      <body>
       ...
      </body>
</html>`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
      </section>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Атрибуты тегов</h3>
      <p className={styles.description}>
        Атрибуты позволяют изменять свойства и поведение элемента, для которого они заданы. Общее количество атрибутов
        достаточно велико, но их значения, как правило, можно сгруппировать по разным типам, например, задающих размер,
        адрес и др.
      </p>
      <p className={styles.description}>
        Атрибуты записываются внутри открывающего тега, а их значения располагаются внутри кавычек. Атрибутов может быть
        несколько. Некоторые теги не могут использоваться без атрибутов.
      </p>
      <p className={styles.description}>
        Атрибуты тега <code className={styles.code}>a</code>:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>href</code> — указывает адрес на ту страницу, на которую перейдет пользователь
          при нажатии на текст ссылки
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>target</code> — указывает то, в каком окне откроется новая страница
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>title</code> — добавляет всплывающую подсказку к тексту ссылки
        </li>
      </ul>
      <p className={styles.description}>
        Атрибуты тега <code className={styles.code}>img</code>:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>src</code> — указывает на адрес изображения
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>alt</code> — указывает альтернативный текст, который будет показан, если
          картинка, по какой-то причине не загрузилась
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>width</code> и <code className={styles.code}>height</code> — задают ширину и
          высоту изображения
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_3_2"
        src="https://codepen.io/rotmanyan/embed/WNZeaWL?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.notificationWarn}>
        Для того, чтобы узнать доступные атрибуты для тега и их назначение, достаточно набрать в поисковике название
        тега. Или напрямую зайти в{' '}
        <a className={styles.link} href="http://htmlreference.io" target="_blank" rel="noopener noreferrer">
          справочник
        </a>
        .
      </p>
    </section>
  </section>
))
